import React, { useState, useCallback } from 'react';
import { Sparkles, ChevronDown, ChevronRight, Users, ListTodo, Clock, Loader2, AlertCircle, Wand2, FileText, AlertTriangle, Calendar, Lock, Upload, LayoutTemplate, Monitor, Globe, Megaphone, CalendarDays, Settings, ArrowLeft } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DatePicker, parseLocalDate, formatDateToISO } from '../ui/date-picker';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { generateProjectPlan, createProjectFromAIPlan } from '../../services/aiProjectService';
import type { AIGeneratedPlan } from '../../data/projectTemplates';
import { getAllTemplates, getTemplateByKey } from '../../data/projectTemplates';
import { useProject } from '../../contexts/ProjectContext';
import { useUIState } from '../../contexts/UIStateContext';
import { useAuth } from '../../contexts/AuthContext';
import analytics from '../../services/analyticsService';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { getMinPlanDisplayName } from '../../utils/featureConfig';
import PricingPage from '../billing/PricingPage';
import ImportDialog from '../import/ImportDialog';
import { toast } from 'sonner';
import { useCopy, fill } from '../../i18n';
import { plural } from '../../i18n/format';

type Step = 'choose' | 'input' | 'loading' | 'preview' | 'template';

interface AIProjectModalProps {
  onClose: () => void;
  onProjectCreated?: (projectId: string) => void;
  /** Abre o modal direto num passo (ex.: 'template' para o seed de onboarding). */
  initialStep?: Step;
  /**
   * Libera a galeria de modelos mesmo no plano Free. Conteúdo estático, sem custo de IA.
   * Usado no primeiro projeto (onboarding) para nunca cair no Gantt vazio. IA e Importar
   * seguem gated como upsell.
   */
  allowTemplatesFree?: boolean;
  /** Pré-seleciona um modelo na galeria (chave de projectTemplates). */
  initialTemplateKey?: string;
}

const TEMPLATE_ICONS: Record<string, React.ElementType> = {
  software: Monitor,
  website: Globe,
  marketing: Megaphone,
  evento: CalendarDays,
  implantacao: Settings,
};

/**
 * `simple`/`medium`/`complex` é o que a Edge Function recebe e o que o estado
 * compara: fica aqui. Rótulo e detalhe vêm do dicionário, pela mesma chave.
 */
const COMPLEXITY_VALUES = ['simple', 'medium', 'complex'] as const;

/**
 * Número de semanas enviado como prazo alvo. É valor, não rótulo: 24, 36 e 52
 * ganham um texto próprio no dicionário porque o produto mostra a equivalência
 * em meses/ano ao lado.
 */
const TARGET_WEEKS = ['4', '6', '8', '10', '12', '16', '20', '24', '36', '52'] as const;

/**
 * Cartão de um dos quatro caminhos do passo inicial.
 *
 * Era o mesmo bloco copiado quatro vezes, e o custo apareceu: os quatro tinham
 * ícone de 32px (a escala do produto vai até 20), `border-2` onde o resto usa
 * 1px, e um selo de plano em 10px, abaixo do piso de 11. Numa grade de quatro
 * colunas dentro de um modal de 720px sobravam ~156px por cartão, o que
 * quebrava "Projeto em branco" em duas linhas e a descrição em quatro.
 *
 * Agora é 2x2 com o cartão deitado (ícone à esquerda, texto à direita): cada um
 * ganha ~324px, título e descrição cabem numa linha cada, e o cadeado do plano
 * bloqueado fica no ícone em vez de virar um terceiro aviso no canto. O selo do
 * canto saiu de propósito: com o cadeado no ícone E "Disponível no plano X" na
 * descrição, ele era a terceira vez que a mesma coisa era dita.
 */
function PathCard({ testId, icon: Icon, title, description, onClick, locked = false, highlighted = false }: {
  testId: string;
  icon: LucideIcon;
  title: string;
  description: string;
  onClick: () => void;
  locked?: boolean;
  highlighted?: boolean;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors',
        'hover:border-[var(--color-primary)] hover:bg-[var(--color-bg-secondary)]',
        // O tom vem do token, nao do modificador de opacidade: no Tailwind 3
        // `bg-[var(--color-primary)]/5` nao pinta NADA (medido: rgba(0,0,0,0)),
        // porque o modificador nao sabe abrir um `var()` arbitrario em canais.
        // Era o que estava aqui, e o realce do caminho recomendado nunca
        // apareceu. `--color-primary-bg` ja existe e resolve nos dois temas.
        highlighted
          ? 'border-[var(--color-primary)] bg-[var(--color-primary-bg)]'
          : 'border-[var(--color-border)]'
      )}
    >
      <span className="relative mt-0.5 shrink-0">
        <Icon
          size={18}
          className={highlighted ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-secondary)]'}
        />
        {locked && (
          <Lock size={11} className="absolute -bottom-1 -right-1 text-[var(--color-text-muted)]" />
        )}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-[var(--color-text)]">{title}</span>
        <span className="mt-0.5 block text-[13px] text-[var(--color-text-muted)]">{description}</span>
      </span>
    </button>
  );
}

const AIProjectModal = ({ onClose, onProjectCreated, initialStep = 'choose', allowTemplatesFree = false, initialTemplateKey }: AIProjectModalProps) => {
  const t = useCopy().modals.ai;
  const { user } = useAuth();
  const { registerCreatedProject } = useProject();
  const { changeModule } = useUIState();
  const { hasFeature, isSystemAdmin } = useSubscription();
  const canUseAI = isSystemAdmin || hasFeature('ai_project_creation');
  const canImport = isSystemAdmin || hasFeature('import_external');
  // Galeria de modelos liberada: durante o trial (canUseAI) ou no seed de onboarding (allowTemplatesFree).
  const templatesUnlocked = canUseAI || allowTemplatesFree;

  // State
  const [step, setStep] = useState<Step>(initialStep);
  // Modelo escolhido de propósito na galeria (vs. fallback automático da IA).
  const [pickedTemplate, setPickedTemplate] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [description, setDescription] = useState('');
  const [complexity, setComplexity] = useState<'simple' | 'medium' | 'complex'>('medium');
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  const [targetWeeks, setTargetWeeks] = useState<string>('none');
  const [plan, setPlan] = useState<AIGeneratedPlan | null>(null);
  const [source, setSource] = useState<'ai' | 'template'>('ai');
  const [warning, setWarning] = useState<string | null>(null);
  const [loadingMsg, setLoadingMsg] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(new Set());

  // Gerar plano
  const handleGenerate = useCallback(async () => {
    if (description.trim().length < 10) {
      setError(t.errors.minDescription);
      return;
    }

    setStep('loading');
    setError(null);
    setLoadingMsg(0);

    // Animar mensagens de loading
    const interval = setInterval(() => {
      setLoadingMsg(prev => (prev + 1) % t.loadingMessages.length);
    }, 2000);

    try {
      const result = await generateProjectPlan(
        description.trim(),
        complexity,
        targetWeeks !== 'none' ? parseInt(targetWeeks) : undefined
      );
      setPlan(result.plan);
      setSource(result.source);
      setWarning(result.warning || null);
      // Expandir todas as fases no preview
      setExpandedPhases(new Set(result.plan.phases.map((_, i) => i)));
      setStep('preview');

      // Feedback visual sobre a fonte
      if (result.source === 'template') {
        toast.warning(result.warning || t.toasts.templateFallback);
      } else if (result.source === 'ai') {
        toast.success(t.toasts.aiSuccess);
      }
    } catch (err) {
      setError((err as Error).message);
      setStep('input');
    } finally {
      clearInterval(interval);
    }
  }, [description, complexity, targetWeeks, t]);

  // Criar projeto
  const handleCreateProject = useCallback(async () => {
    if (!plan || !user) return;

    setCreating(true);
    setError(null);

    try {
      const result = await createProjectFromAIPlan(
        plan,
        user.id,
        new Date(startDate + 'T12:00:00')
      );

      toast.success(fill(t.toasts.created, {
        tasks: fill(plural(result.taskCount, t.toasts.createdTasks), { count: result.taskCount }),
        members: fill(plural(result.teamCount, t.toasts.createdMembers), { count: result.teamCount }),
      }));

      // Projeto criado por IA/modelo (não passa pelo ProjectContext.createProject,
      // então não há risco de double-count com o project_created genérico).
      analytics.trackAIProjectCreated(plan.project.name, source);

      // Registra o projeto recém-criado direto no contexto (sem re-consultar o
      // servidor). Mantém projects.length >= 1 antes do loadProjectData, evitando o
      // spinner roxo de tela cheia e o ciclo de carga dupla do onboarding.
      registerCreatedProject(result.project);
      changeModule('gantt');
      onProjectCreated?.(result.projectId);
      onClose();
    } catch (err) {
      setError((err as Error).message);
      setCreating(false);
    }
  }, [plan, user, startDate, registerCreatedProject, changeModule, onProjectCreated, onClose, t]);

  // Toggle fase expandida
  const togglePhase = (idx: number) => {
    setExpandedPhases(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // Contar total de tarefas e estimar duração
  const totalTasks = plan?.phases.reduce((sum, p) => sum + p.tasks.length, 0) || 0;
  const estimatedDays = plan?.phases.reduce((sum, phase) => {
    const phaseDays = phase.tasks.reduce((s, t) => {
      const match = t.duration.match(/^(\d+)d$/);
      return s + (match ? parseInt(match[1]) : 0);
    }, 0);
    return sum + phaseDays;
  }, 0) || 0;

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open && !creating) onClose(); }}>
      <DialogContent className="max-w-[720px] p-0 gap-0 max-h-[90vh] flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-[var(--color-border)]">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-[var(--color-primary)]" />
            {step === 'choose' && t.title.choose}
            {step === 'template' && t.title.template}
            {step === 'input' && t.title.input}
            {step === 'loading' && t.title.loading}
            {step === 'preview' && t.title.preview}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Step: Choose */}
          {step === 'choose' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PathCard
                testId="new-project-path-blank"
                icon={FileText}
                title={t.paths.blank.title}
                description={t.paths.blank.description}
                onClick={() => {
                  onClose();
                  // Emitir evento para abrir input inline na sidebar
                  window.dispatchEvent(new CustomEvent('create-blank-project'));
                }}
              />

              <PathCard
                testId="new-project-path-ai"
                icon={Sparkles}
                title={t.paths.ai.title}
                description={canUseAI ? t.paths.ai.description : fill(t.lockedPlan, { plan: getMinPlanDisplayName('ai_project_creation') })}
                onClick={() => canUseAI ? setStep('input') : setShowPricing(true)}
                locked={!canUseAI}
                highlighted={canUseAI}
              />

              <PathCard
                testId="new-project-path-import"
                icon={Upload}
                title={t.paths.import.title}
                description={canImport ? t.paths.import.description : fill(t.lockedPlan, { plan: getMinPlanDisplayName('import_external') })}
                onClick={() => canImport ? setShowImportDialog(true) : setShowPricing(true)}
                locked={!canImport}
              />

              <PathCard
                testId="new-project-path-template"
                icon={LayoutTemplate}
                title={t.paths.template.title}
                description={templatesUnlocked ? t.paths.template.description : fill(t.lockedPlan, { plan: getMinPlanDisplayName('ai_project_creation') })}
                onClick={() => templatesUnlocked ? setStep('template') : setShowPricing(true)}
                locked={!templatesUnlocked}
              />
            </div>
          )}

          {/* Step: Template Gallery */}
          {step === 'template' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setStep('choose')}
                  className="flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"
                >
                  <ArrowLeft size={14} />
                  {t.back}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {getAllTemplates()
                  .slice()
                  .sort((a, b) => (a.key === initialTemplateKey ? -1 : b.key === initialTemplateKey ? 1 : 0))
                  .map(tmpl => {
                  const Icon = TEMPLATE_ICONS[tmpl.key] || FileText;
                  const isRecommended = !!initialTemplateKey && tmpl.key === initialTemplateKey;
                  return (
                    <button
                      key={tmpl.key}
                      type="button"
                      data-testid={`template-card-${tmpl.key}`}
                      onClick={() => {
                        const template = getTemplateByKey(tmpl.key);
                        if (template) {
                          setPlan(template);
                          setSource('template');
                          setPickedTemplate(true);
                          setExpandedPhases(new Set(template.phases.map((_, i) => i)));
                          setStep('preview');
                        }
                      }}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 hover:border-[var(--color-primary)] hover:bg-[var(--color-bg-secondary)] hover:shadow-md transition-all cursor-pointer text-left relative ${isRecommended ? 'border-[var(--color-primary)] bg-[var(--color-primary-bg)]' : 'border-[var(--color-border)]'}`}
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[var(--color-primary-bg)] shrink-0">
                        <Icon className="w-5 h-5 text-[var(--color-primary)]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[var(--color-text)] text-sm">{tmpl.name}</p>
                        <p className="text-[13px] text-[var(--color-text-secondary)] mt-0.5 line-clamp-2">{tmpl.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-[11px] text-[var(--color-text-muted)]">
                          <span className="flex items-center gap-1"><ListTodo size={11} />{fill(plural(tmpl.phaseCount, t.templateStats.phases), { count: tmpl.phaseCount })}</span>
                          <span className="flex items-center gap-1"><Clock size={11} />{fill(plural(tmpl.taskCount, t.templateStats.tasks), { count: tmpl.taskCount })}</span>
                          <span className="flex items-center gap-1"><Users size={11} />{fill(plural(tmpl.teamCount, t.templateStats.members), { count: tmpl.teamCount })}</span>
                        </div>
                      </div>
                      {isRecommended && (
                        <span className="absolute top-2 right-2 text-[11px] px-1.5 py-0.5 rounded bg-[var(--color-primary)] text-white font-medium">
                          {t.recommended}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step: Input */}
          {step === 'input' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[var(--color-text)] mb-1.5 block">
                  {t.input.descriptionLabel}
                </label>
                <Textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder={t.input.descriptionPlaceholder}
                  className="min-h-[160px] resize-none"
                  maxLength={3000}
                  autoFocus
                />
                <p className="text-[11px] text-[var(--color-text-muted)] mt-1 text-right">
                  {description.length}/3000
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-[var(--color-text)] mb-1.5 block">
                  {t.input.complexityLabel}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {COMPLEXITY_VALUES.map(value => (
                    <button
                      type="button"
                      key={value}
                      onClick={() => setComplexity(value)}
                      className={`p-3 rounded-lg border-2 text-left transition-all cursor-pointer ${
                        complexity === value
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary-bg)]'
                          : 'border-[var(--color-border)] hover:border-[var(--color-primary)] hover:bg-[var(--color-bg-secondary)]'
                      }`}
                    >
                      <p className="text-sm font-medium text-[var(--color-text)]">{t.complexity[value].label}</p>
                      <p className="text-[13px] text-[var(--color-text-secondary)] mt-0.5">{t.complexity[value].description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[var(--color-text)] mb-1.5 block">
                    {t.input.startDateLabel}
                  </label>
                  <DatePicker
                    value={parseLocalDate(startDate)}
                    onChange={(d) => setStartDate(formatDateToISO(d))}
                    placeholder={t.input.startDatePlaceholder}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[var(--color-text)] mb-1.5 block">
                    {t.input.targetLabel}
                  </label>
                  <Select value={targetWeeks} onValueChange={setTargetWeeks}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t.target.none}</SelectItem>
                      {TARGET_WEEKS.map(weeks => (
                        <SelectItem key={weeks} value={weeks}>
                          {weeks === '24' ? t.target.weeks24
                            : weeks === '36' ? t.target.weeks36
                            : weeks === '52' ? t.target.weeks52
                            : fill(t.target.weeks, { count: weeks })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Step: Loading */}
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-12 gap-6">
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-[var(--color-primary-bg)] flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-[var(--color-primary)] animate-spin" />
                </div>
                <Sparkles className="w-5 h-5 text-[var(--color-primary)] absolute -top-1 -right-1 animate-pulse" />
              </div>
              <div className="text-center">
                <p className="text-[var(--color-text)] font-medium">
                  {t.loadingMessages[loadingMsg]}
                </p>
                <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                  {t.loadingHint}
                </p>
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === 'preview' && plan && (
            <div className="space-y-4">
              {/* Header com métricas */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-[var(--color-text)]">{plan.project.name}</h3>
                  <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">{plan.project.description}</p>
                </div>
                {source === 'template' ? (
                  <span className="text-[11px] px-2 py-1 rounded bg-[var(--color-warning-bg)] text-[var(--color-warning-text)] flex-shrink-0 flex items-center gap-1">
                    <AlertTriangle size={11} />
                    {t.preview.badgeTemplate}
                  </span>
                ) : (
                  <span className="text-[11px] px-2 py-1 rounded bg-[var(--color-success-bg)] text-[var(--color-success-text)] flex-shrink-0 flex items-center gap-1">
                    <Sparkles size={11} />
                    {t.preview.badgeAI}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-3 text-sm text-[var(--color-text-secondary)]">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {fill(plural(plan.team.length, t.preview.members), { count: plan.team.length })}
                </span>
                <span className="flex items-center gap-1">
                  <ListTodo className="w-4 h-4" />
                  {fill(plural(totalTasks, t.preview.tasks), { count: totalTasks })}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {fill(plural(plan.phases.length, t.preview.phases), { count: plan.phases.length })}
                </span>
                {estimatedDays > 0 && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {fill(plural(estimatedDays, t.preview.workDays), { count: estimatedDays })}
                  </span>
                )}
              </div>

              {/* Alerta quando a IA falhou e caiu num template (não quando o usuário escolheu de propósito) */}
              {source === 'template' && !pickedTemplate && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">{t.preview.fallbackTitle}</p>
                    <p className="text-amber-700 mt-0.5">
                      {warning || t.preview.fallbackReason} {t.preview.fallbackRetry}
                    </p>
                  </div>
                </div>
              )}

              {/* Árvore de fases e tarefas */}
              <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
                {plan.phases.map((phase, phaseIdx) => (
                  <div key={phaseIdx} className={phaseIdx > 0 ? 'border-t border-[var(--color-border)]' : ''}>
                    <button
                      onClick={() => togglePhase(phaseIdx)}
                      className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[var(--color-bg-secondary)] transition-colors text-left"
                    >
                      {expandedPhases.has(phaseIdx) ? (
                        <ChevronDown className="w-4 h-4 text-[var(--color-text-secondary)]" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-[var(--color-text-secondary)]" />
                      )}
                      <span
                        className="w-3 h-3 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: phase.color }}
                      />
                      <span className="font-medium text-sm text-[var(--color-text)]">{phase.name}</span>
                      <span className="text-[11px] text-[var(--color-text-muted)] ml-auto">
                        {fill(plural(phase.tasks.length, t.preview.items), { count: phase.tasks.length })}
                      </span>
                    </button>

                    {expandedPhases.has(phaseIdx) && (
                      <div className="bg-[var(--color-bg-secondary)]/50">
                        {phase.tasks.map((task, taskIdx) => (
                          <div
                            key={taskIdx}
                            className="flex items-center gap-2 px-3 py-1.5 pl-10 text-sm border-t border-[var(--color-border)]/50"
                          >
                            {task.type === 'milestone' ? (
                              <span className="w-3 h-3 rotate-45 bg-[var(--color-primary)] flex-shrink-0" />
                            ) : (
                              <span className="w-3 h-3 rounded-full border-2 border-[var(--color-text-secondary)]/40 flex-shrink-0" />
                            )}
                            <span className={`flex-1 ${task.type === 'milestone' ? 'font-medium text-[var(--color-primary)]' : 'text-[var(--color-text)]'}`}>
                              {task.name}
                            </span>
                            {task.duration !== '0d' && (
                              <span className="text-[11px] text-[var(--color-text-muted)]">{task.duration}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Equipe */}
              <div>
                <p className="text-sm font-medium text-[var(--color-text)] mb-2">{t.preview.teamTitle}</p>
                <div className="flex flex-wrap gap-2">
                  {plan.team.map((member, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-white"
                      style={{ backgroundColor: member.avatar_color }}
                    >
                      {member.role_title}
                    </span>
                  ))}
                </div>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 'choose' && step !== 'loading' && step !== 'template' && (
          <DialogFooter className="px-6 py-4 border-t border-[var(--color-border)]">
            {step === 'input' && (
              <>
                <Button variant="ghost" onClick={() => setStep('choose')}>
                  {t.back}
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={description.trim().length < 10}
                  className="gap-2"
                >
                  <Wand2 className="w-4 h-4" />
                  {t.actions.generate}
                </Button>
              </>
            )}

            {step === 'preview' && (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setPlan(null);
                    if (pickedTemplate) {
                      setPickedTemplate(false);
                      setStep('template');
                    } else {
                      setStep('input');
                    }
                  }}
                  disabled={creating}
                >
                  {pickedTemplate ? t.actions.changeTemplate : t.actions.regenerate}
                </Button>
                <Button
                  data-testid="create-project-confirm"
                  onClick={handleCreateProject}
                  disabled={creating}
                  className="gap-2"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t.actions.creating}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      {t.actions.create}
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
      {showPricing && <PricingPage onClose={() => setShowPricing(false)} />}
      <ImportDialog
        open={showImportDialog}
        onOpenChange={(open) => {
          setShowImportDialog(open);
          if (!open) onClose();
        }}
      />
    </Dialog>
  );
};

export default AIProjectModal;
